import { ViewEmployee } from '@xeetr/types/models'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Link } from 'react-router-dom'

import FloatingLabel from '../../inputs/FloatingLabel'
import LabeledInputText from '../../inputs/LabeledInputText'
import CustomFileUpload from '../common/CustomFileUpload'

import { MultiSelect } from '@/components/ui/multi-select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useServices } from '@/hooks'

const EmployeeInfo = () => {
  const { control } = useFormContext<ViewEmployee>()
  const { services } = useServices()
  const employeeId = useWatch({ control, name: 'id' })

  return (
    <div className="space-y-8">
      {/* Profile Image & Basic Information */}
      <section>
        <h3 className="mb-4 text-lg font-semibold">Grundinformationen</h3>
        <div className="grid gap-6 md:grid-cols-[300px_1fr]">
          {/* Profile Image - Left Column */}
          <div>
            <Controller
              name="info.pictureUrl"
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <CustomFileUpload
                    src={field.value || ''}
                    onChange={field.onChange}
                    url={`service/management/employee/${employeeId}/image`}
                    alt="Mitarbeiterbild"
                    chooseLabel="Neues Mitarbeiterbild hochladen"
                    message="Neues Mitarbeiterbild erfolgreich hochgelden."
                    placeholderIcon="user"
                    disabled={!employeeId}
                    disabledMessage="Bitte speichern Sie den Mitarbeiter zuerst, bevor Sie ein Bild hochladen können."
                  />
                  {!employeeId && (
                    <p className="text-muted-foreground text-center text-xs">
                      Bitte speichern Sie den Mitarbeiter zuerst, bevor Sie ein Bild hochladen.
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          {/* Basic Info - Right Column */}
          <div className="grid gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <LabeledInputText
                    label="Name"
                    id={field.name}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name="info.mail"
                render={({ field }) => (
                  <LabeledInputText
                    label="E-Mail"
                    id={field.name}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <Controller
              name="services"
              control={control}
              render={({ field, fieldState }) => (
                <FloatingLabel label="Dienstleistungen">
                  <MultiSelect
                    className={cn('w-full', {
                      'p-invalid': fieldState.error,
                    })}
                    value={field.value?.map((service) => service.id)}
                    onValueChange={(selectedIds) => {
                      const changed = services.filter((service) => selectedIds.includes(service.id))
                      field.onChange(changed)
                    }}
                    options={services.map((service) => ({
                      value: service.id,
                      label: service.name,
                    }))}
                    placeholder="Wähle Dienstleistungen aus."
                    emptyState={
                      <div>
                        <p>Es wurden noch keine Dienstleistung erstellt.</p>
                        <p>
                          <Link to="/settings/services" className="text-blue-600 underline">
                            Klicken Sie hier, um welche hinzuzufügen.
                          </Link>
                        </p>
                      </div>
                    }
                  />
                </FloatingLabel>
              )}
            />
          </div>
        </div>
      </section>

      {/* Services & Vita - Side by Side */}
      <section>
        <h3 className="mb-4 text-lg font-semibold">Vita</h3>
        <Controller
          control={control}
          name="info.vita"
          render={({ field }) => (
            <FloatingLabel label="Vita">
              <Textarea {...field} rows={5} />
            </FloatingLabel>
          )}
        />
      </section>
    </div>
  )
}

export default EmployeeInfo
